/**
 * Company SSO login step.
 *
 * After the SAML callback exchanges the code for a session, the shell calls
 * `establish_employee_session()` which JIT-creates the principal, mints
 * mapped membership for bound Entra groups, and hard-denies when the
 * principal ends up with no membership at all — neither mapped nor
 * admin-minted (the database deletes the employee sessions in that case).
 * This module holds the pure decision logic so the route stays thin and
 * testable.
 */

export const employeeAccessDeniedPath = "/access-denied";
export const employeeAccessPendingPath = "/access-pending";

export type EmployeeLoginStatus =
  | "bound"
  | "unbound"
  /** A platform principal without a confirmed policy bridge; same hard deny. */
  | "unbridged"
  | "not_enterprise"
  | "unauthenticated"
  | "error";

export interface EmployeeLoginResult {
  status: EmployeeLoginStatus;
  orgIds: string[];
  activeOrgId: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function parseEmployeeLoginResult(value: unknown): EmployeeLoginResult {
  if (!isRecord(value) || typeof value.status !== "string") {
    return { status: "error", orgIds: [], activeOrgId: null };
  }

  const status: EmployeeLoginStatus =
    value.status === "bound" ||
    value.status === "unbound" ||
    value.status === "unbridged" ||
    value.status === "not_enterprise" ||
    value.status === "unauthenticated"
      ? value.status
      : "error";

  const orgIds = Array.isArray(value.org_ids)
    ? value.org_ids.filter((entry): entry is string => typeof entry === "string")
    : [];

  return {
    status,
    orgIds,
    activeOrgId: typeof value.active_org_id === "string" ? value.active_org_id : null,
  };
}

export interface EmployeeLoginDecision {
  destination: string;
  /** True when no product session may survive (hard deny or failure). */
  endSession: boolean;
  /**
   * True when membership was minted during this login step, so the access
   * token issued by the code exchange predates the routing claims and must be
   * refreshed before the operator shell reads it.
   */
  refreshClaims: boolean;
}

export function decideEmployeeLoginDestination({
  result,
  next,
}: {
  result: EmployeeLoginResult;
  next: string;
}): EmployeeLoginDecision {
  switch (result.status) {
    case "bound":
      return { destination: next, endSession: false, refreshClaims: true };
    case "not_enterprise":
      // Password sessions are a local/preview convenience; the database plane
      // resolver fails them closed everywhere else.
      return { destination: next, endSession: false, refreshClaims: false };
    case "unbound":
    case "unbridged":
      return { destination: employeeAccessDeniedPath, endSession: true, refreshClaims: false };
    default:
      return {
        destination: "/sign-in?status=confirmation_failed",
        endSession: true,
        refreshClaims: false,
      };
  }
}

/** True when the JWT was issued for an enterprise SAML sign-in. */
export function isEnterpriseSessionClaims(claims: unknown): boolean {
  if (!isRecord(claims) || !Array.isArray(claims.amr)) {
    return false;
  }
  return claims.amr.some((entry) => isRecord(entry) && entry.method === "sso/saml");
}
